'use client';

import { PublicShell } from '@/components/layout/PublicShell';
import { FormEvent, useState } from 'react';

export default function RegisterPage() {
  const [email, setEmail] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [password, setPassword] = useState('');
  const [message, setMessage] = useState('');
  const [registeredEmail, setRegisteredEmail] = useState<string | null>(null);
  // Closed alpha: the backend answers 'registration_pending_approval' and only sends the
  // verification email once an admin approves the registration.
  const [pendingApproval, setPendingApproval] = useState(false);
  const [loading, setLoading] = useState(false);

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();

    setLoading(true);
    setMessage('');

    try {
      const response = await fetch(`${process.env.NEXT_PUBLIC_API_URL}/auth/register`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          email,
          password,
          displayName,
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || 'Sikertelen regisztráció');
      }

      setPendingApproval(data.message === 'registration_pending_approval');
      setRegisteredEmail(data.email ?? email);
      setEmail('');
      setDisplayName('');
      setPassword('');
    } catch (error) {
      setMessage(error instanceof Error ? error.message : 'Ismeretlen hiba');
    } finally {
      setLoading(false);
    }
  }

  if (registeredEmail) {
    return (
      <PublicShell>
        <main className="min-h-screen flex items-center justify-center p-6">
          <div className="w-full max-w-md rounded-2xl border p-6 space-y-3 shadow-sm text-center">
            {pendingApproval ? (
              <>
                <h1 className="text-2xl font-bold">Köszönjük a regisztrációt!</h1>
                <p className="text-sm text-muted-foreground">
                  Az Anchor jelenleg zárt tesztelés alatt áll, ezért minden regisztrációt
                  egyenként hagyunk jóvá. Jóváhagyás után megerősítő linket küldünk a(z){' '}
                  <strong>{registeredEmail}</strong> címre.
                </p>
              </>
            ) : (
              <>
                <h1 className="text-2xl font-bold">Ellenőrizd az emailjeidet</h1>
                <p className="text-sm text-muted-foreground">
                  Küldtünk egy megerősítő linket a(z) <strong>{registeredEmail}</strong> címre.
                  A fiókod aktiválásához kattints a linkre.
                </p>
              </>
            )}
          </div>
        </main>
      </PublicShell>
    );
  }

  return (
    <PublicShell>
      <main className="min-h-screen flex items-center justify-center p-6">
        <form
          onSubmit={handleSubmit}
          className="w-full max-w-md rounded-2xl border p-6 space-y-4 shadow-sm"
        >
          <h1 className="text-2xl font-bold">Regisztráció</h1>

          <label className="block space-y-1">
            <span className="text-sm font-medium">Email</span>
            <input
              className="w-full rounded-xl border px-3 py-2"
              type="email"
              value={email}
              onChange={(event) => setEmail(event.target.value)}
              required
            />
          </label>

          <label className="block space-y-1">
            <span className="text-sm font-medium">Megjelenített név</span>
            <input
              className="w-full rounded-xl border px-3 py-2"
              value={displayName}
              onChange={(event) => setDisplayName(event.target.value)}
            />
          </label>

          <label className="block space-y-1">
            <span className="text-sm font-medium">Jelszó</span>
            <input
              className="w-full rounded-xl border px-3 py-2"
              type="password"
              value={password}
              onChange={(event) => setPassword(event.target.value)}
              required
              minLength={6}
            />
          </label>

          <button
            type="submit"
            disabled={loading}
            className="w-full rounded-xl bg-primary text-primary-foreground px-4 py-2 font-medium disabled:opacity-60"
          >
            {loading ? 'Regisztráció...' : 'Regisztrálok'}
          </button>

          {message && (
            <pre className="whitespace-pre-wrap rounded-xl bg-muted px-3 py-2 text-sm text-foreground">
              {message}
            </pre>
          )}
        </form>
      </main>
    </PublicShell>
  );
}
